import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { LoginResponse } from '@ace-tracker/shared';

const STORAGE_KEY = 'ace-dashboard-token';

interface AuthState {
  token: string | null;
  username: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  /** Swaps in a new session token (after a password change) without signing out. */
  replaceSession: (session: LoginResponse) => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));
  const [username, setUsername] = useState<string | null>(() => localStorage.getItem(`${STORAGE_KEY}-user`));

  const value = useMemo<AuthState>(
    () => ({
      token,
      username,
      login: async (u: string, p: string) => {
        const res = await fetch('/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: u, password: p }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({ error: 'Login failed' }));
          throw new Error(body.error ?? 'Login failed');
        }
        const data = (await res.json()) as LoginResponse;
        localStorage.setItem(STORAGE_KEY, data.token);
        localStorage.setItem(`${STORAGE_KEY}-user`, data.username);
        setToken(data.token);
        setUsername(data.username);
      },
      replaceSession: (data: LoginResponse) => {
        localStorage.setItem(STORAGE_KEY, data.token);
        localStorage.setItem(`${STORAGE_KEY}-user`, data.username);
        setToken(data.token);
        setUsername(data.username);
      },
      logout: () => {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(`${STORAGE_KEY}-user`);
        setToken(null);
        setUsername(null);
      },
    }),
    [token, username],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function getStoredToken(): string | null {
  return localStorage.getItem(STORAGE_KEY);
}

export function clearStoredToken(): void {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(`${STORAGE_KEY}-user`);
}
